import {Capacitor} from "@capacitor/core";
import {GenerateUUID} from "mobx-graphlink";
import {ApplyVolumeEffectsToVolumeValue, type PlaySoundOpts, type PlaySoundOpts_Finalized} from "../Bridge/Bridge_Native/MediaPlayer.js";
import {InAndroid} from "../Bridge/Bridge_Native.js";
import {GetAudioFilesWithSubpath} from "../../Store/main/general/CacheState.js";
import {AudioFileEntry} from "../Bridge/Bridge_Native/MediaTypes.js";
import {mockAudioFiles} from "./MockAudioFiles.js";
import {CycleCountType, SoundMutingType, SoundPanningType} from "../../Server/DB/@Shared/EffectPointer.js";
import {GeneralComp} from "../../Engine/LiveSession/Components/GeneralComp.js";
import {liveSession} from "../../Engine/LiveSessionManagement.js";

// Start at 12 o'clock / top of circle (matches SpatialAudioSubcomp reference point)
const PoolState_startingAngle = Math.PI / 2;

// Shared AudioContext singleton -- created on first use, reused by all WebAudioPlayer instances.
let sharedAudioContext: AudioContext|n;
export function GetSharedAudioContext(): AudioContext {
	if (!sharedAudioContext || sharedAudioContext.state === "closed") {
		sharedAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
	}
	if (sharedAudioContext.state === "suspended") {
		sharedAudioContext.resume().catch(()=>{});
	}
	return sharedAudioContext;
}

// Shared forced-pan state written by BreathComp (or whoever calls SetForcedPan) and read by
// active players on their 30ms animation tick. Stored here so all pools can read it without
// needing a reference back to any session/comp.
export let webAudio_forcedPan: "left" | "right" | null = null;
export let webAudio_panStrength = 1;
export function WebAudio_ForcedPan_Set(pan: "left" | "right" | null, strength: number) {
	webAudio_forcedPan = pan;
	webAudio_panStrength = strength;
}
export let webAudio_forcedPan_slow: "left" | "right" | null = null;
export function WebAudio_ForcedPan_Slow_Toggle() {
	// if null-state differs between "regular" and "slow" states, simply mirror the regular state
	if ((webAudio_forcedPan_slow == null) != (webAudio_forcedPan == null)) {
		webAudio_forcedPan_slow = webAudio_forcedPan;
	}
	// else, simply toggle the "slow" state
	else {
		webAudio_forcedPan_slow = webAudio_forcedPan_slow === "left" ? "right" : "left";
	}
}

// Shared forced-mute state written by BreathComp (or whoever calls SetForcedMute) and read by
// active players on their 30ms animation tick. Stored here so all pools can read it without
// needing a reference back to any session/comp.
export let webAudio_forcedMute: boolean | null = null;
export function WebAudio_ForcedMute_Set(mute: boolean | null) {
	webAudio_forcedMute = mute;
}
export let webAudio_forcedMute_slow: boolean | null = null;
export function WebAudio_ForcedMute_Slow_Toggle() {
	// if null-state differs between "regular" and "slow" states, simply mirror the regular state
	if ((webAudio_forcedMute_slow == null) != (webAudio_forcedMute == null)) {
		webAudio_forcedMute_slow = webAudio_forcedMute;
	}
	// else, simply toggle the "slow" state
	else {
		webAudio_forcedMute_slow = !webAudio_forcedMute_slow;
	}
}

/**
 * Select a random audio file from the given subpath.
 * When not running on Android (eg. PC testing), substitutes a public sample file if no
 * local files are found, so the audio pipeline can still be exercised.
 * Returns null if no file is available.
 */
export function SelectAudioFileFromPath_ForWebAudioPlayer(subpath: string): AudioFileEntry|n {
	let selectedAudioFile = GetAudioFilesWithSubpath(subpath).Random();
	// keep this commented, other than when testing on pc
	if (!selectedAudioFile && !InAndroid(0)) {
		selectedAudioFile = mockAudioFiles[0];
	}
	return selectedAudioFile;
}

// ========================
// WebAudioPlayer
// ========================

export type WebAudioPlayer_Opts = {
	/** Note: If this starts with "http", it is used directly as the audio-src.
	 * Else, Capacitor.convertFileSrc is called to convert the android content-uri into a streamable audio url. */
	contentUri: string;
	playOpts: PlaySoundOpts_Finalized;
	//panningType?: SoundPanningType;
	/** Customizes the "spatial" panning-type. */
	spatial?: {
		initialDirection_clockwise?: boolean;
		/** Min seconds between direction reversals. */
		switchDirInterval_min: number;
		/** Max seconds between direction reversals. */
		switchDirInterval_max: number;
		/** Seconds per full revolution. */
		revolutionDuration: number;
		/** Radius of the 3D audio circle around the listener (metres). */
		distance: number;
		/** Optional getter returning a dynamic adjustment offset (seconds) added to revolutionDuration. */
		revolutionDurationAdjustmentGetter?: () => number;
		cycleComplete_countType?: CycleCountType;
		cycleComplete_countStart?: number;
		/** For CycleCountType.count_down_solveTimeLeft: return the new count, or null to suppress
		 *  the announcement for this cycle. */
		cycleComplete_countOverrideGetter?: () => number|null;
		/** Called after a cycle completes and the internal count has been updated.
		 *  The caller is responsible for any TTS announcement / phase gating. */
		onCycleComplete?: (count: number) => void;
	};
	/** If true, taps the gain-node output into a MediaRecorder stream for later retrieval
	 *  via StartRecording() / StopRecording() / GetRecordedAudio(). */
	enableRecording?: boolean;
	blockCleanupOnEnd?: boolean;
};

/** Low-level web-audio player for a single audio file. Mirrors the audio graph from
 *  SpatialAudioSubcomp but is generic: it plays any content-uri and exposes the controls
 *  needed by the MediaPlayer.ts API surface (resume, pause, stop, set-pan). */
export class WebAudioPlayer {
	constructor(opts: WebAudioPlayer_Opts) {
		this.opts = opts;
		this.id = opts.playOpts.playerId ?? GenerateUUID();
	}
	id: string;
	opts: WebAudioPlayer_Opts;

	audioContext?: AudioContext;
	audioElement?: HTMLAudioElement;
	audioSource?: MediaElementAudioSourceNode;
	/** Stereo panner — used in regular (non-spatial) mode and simple-panning spatial mode. */
	stereoPannerNode?: StereoPannerNode;
	/** 3D HRTF panner — used in full spatial mode. */
	pannerNode?: PannerNode;
	gainNode?: GainNode;
	/** Dedicated gain node for muting (gain = 0 when muted, 1 when unmuted).
	 * Separate node, so muting never interferes with regular gainNode (eg. volume-evolve's setValueAtTime calls). */
	muteGainNode?: GainNode;

	/** setTimeout id used to enforce durationLimit */
	private durationLimitTimerID?: ReturnType<typeof setTimeout>;
	/** setInterval id for stereo pan animation (simple-panning mode) */
	private panSimple_animationTimerID?: ReturnType<typeof setInterval>;
	/** setInterval id for muting animation */
	private mute_animationTimerID?: ReturnType<typeof setInterval>;
	/** setInterval id for 3D HRTF spatial animation */
	private panSpatial_animationTimerID?: ReturnType<typeof setInterval>;

	// Spatial animation state (3D HRTF mode only)
	currentAngle = PoolState_startingAngle;
	spatialDirection = 1; // 1 = clockwise, -1 = counter-clockwise
	private lastSwitchTime = 0;
	private nextSwitchDuration = 0;
	cycleCompletionCount?: number;

	/** When set (not undefined), overrides the global webAudio_forcedPan for this player's pan-update
	 *  tick. Used by MediaComp's desync-training feature to apply a deliberately delayed pan value. */
	forcedPanOverride: "left"|"right"|null|undefined = undefined;

	// Completion handlers — called when playback ends naturally ("ended" event) or Stop() is called.
	private oneTimeCompletionListeners: (()=>void)[] = [];
	AddOneTimeCompletionHandler(fn: ()=>void) { this.oneTimeCompletionListeners.push(fn); }
	private FireAndClearCompletionHandlers() {
		const fns = this.oneTimeCompletionListeners.splice(0); // clear the array, while getting the existing handlers to call
		for (const fn of fns) fn();
	}

	// Recording state
	mediaRecorder?: MediaRecorder;
	recordedChunks: Blob[] = [];
	recordingDestinationNode?: MediaStreamAudioDestinationNode;
	isRecording = false;

	/** Called by the pool when playback should start. Returns this player for chaining. */
	async Start(): Promise<this> {
		const {opts} = this;

		try {
			// graph construction/connections (plus some of the very basic/single-line configurations)
			// ==========

			this.audioContext = GetSharedAudioContext();

			const audioUrl = opts.contentUri.startsWith("http")
				? opts.contentUri
				: Capacitor.convertFileSrc(opts.contentUri);
			this.audioElement = new Audio(audioUrl);
			this.audioElement.loop = opts.playOpts.loop;
			this.audioElement.crossOrigin = "anonymous";
			this.audioSource = this.audioContext.createMediaElementSource(this.audioElement);

			this.gainNode = this.audioContext.createGain();
			this.gainNode.gain.value = opts.playOpts.volume_final;
			this.muteGainNode = this.audioContext.createGain();
			this.muteGainNode.gain.value = 1;
			this.gainNode.connect(this.muteGainNode);

			if (opts.playOpts.panningType == SoundPanningType.simple || opts.playOpts.panningType == SoundPanningType.simple_slow) {
				// Stereo audio graph: source -> stereoPannerNode -> gain
				this.stereoPannerNode = this.audioContext.createStereoPanner();
				this.stereoPannerNode.pan.value = 0;
				this.audioSource.connect(this.stereoPannerNode);
				this.stereoPannerNode.connect(this.gainNode);
			} else if (opts.playOpts.panningType == SoundPanningType.spatial) {
				// 3D HRTF audio graph: source -> pannerNode -> gain
				this.pannerNode = this.audioContext.createPanner();
				this.pannerNode.panningModel = "HRTF";
				this.pannerNode.distanceModel = "inverse";
				this.pannerNode.refDistance = 1;
				this.pannerNode.maxDistance = 1000;
				this.audioSource.connect(this.pannerNode);
				this.pannerNode.connect(this.gainNode);
				// Place listener at origin
				const listener = this.audioContext.listener;
				if (listener.positionX) {
					listener.positionX.value = 0;
					listener.positionY.value = 0;
					listener.positionZ.value = 0;
				} else {
					(listener as any).setPosition(0, 0, 0);
				}
			} else {
				// No panning: direct audio graph: source -> gain
				this.audioSource.connect(this.gainNode);
			}

			if (opts.enableRecording) {
				this.recordingDestinationNode = this.audioContext.createMediaStreamDestination();
				this.muteGainNode.connect(this.recordingDestinationNode);
			}
			this.muteGainNode.connect(this.audioContext.destination);

			// additional setup (eg. timers, and pre-assigning node values at given times)
			// ==========

			// Apply start-time offset (pick random value within [min, max])
			const startTime = opts.playOpts.startTime_min > 0 || opts.playOpts.startTime_max > 0
				? opts.playOpts.startTime_min + Math.random() * Math.max(0, opts.playOpts.startTime_max - opts.playOpts.startTime_min)
				: 0;
			if (startTime > 0) {
				this.audioElement.currentTime = startTime;
			}

			// Apply playback rate if configured
			this._ApplyPlaybackRate();

			// Apply volume evolution (discrete steps at each interval tick) if configured
			if (opts.playOpts.volume_evolveValues_final?.length && opts.playOpts.volume_evolveInterval) {
				const values = opts.playOpts.volume_evolveValues_final;
				const interval = opts.playOpts.volume_evolveInterval;
				const offset = opts.playOpts.volume_evolveOffset ?? 0;
				const now = this.audioContext.currentTime;
				// Shift every step's scheduled time by the offset (seconds already evolved in prior runs).
				// Steps whose shifted time <= 0 are already past; track the last such value as the starting gain.
				let initialGainOverride: number|n = null;
				for (let i = 0; i < values.length; i++) {
					const timeFromNow = ((i + 1) * interval) - offset;
					if (timeFromNow <= 0) {
						initialGainOverride = values[i];
					} else {
						this.gainNode.gain.setValueAtTime(values[i], now + timeFromNow);
					}
				}
				if (initialGainOverride != null) {
					this.gainNode.gain.value = initialGainOverride;
				}
			}

			if (opts.playOpts.panningType == SoundPanningType.simple || opts.playOpts.panningType == SoundPanningType.simple_slow) {
				// Stereo pan animation (reads global webAudio_forcedPan every 30ms)
				this.panSimple_animationTimerID = setInterval(()=>this._UpdatePan(), 30);
			} else if (opts.playOpts.panningType == SoundPanningType.spatial) {
				this._StartSpatialAnimation(opts.spatial!);
			}

			// Muting animation (reads global webAudio_forcedMute every 30ms)
			if (opts.playOpts.mutingType == SoundMutingType.simple || opts.playOpts.mutingType == SoundMutingType.simple_slow) {
				this.mute_animationTimerID = setInterval(()=>this._UpdateMute(), 30);
			}

			// Enforce duration limit
			if (opts.playOpts.durationLimit > 0) {
				this.durationLimitTimerID = setTimeout(()=>this.StopAndCleanup(), opts.playOpts.durationLimit * 1000);
			}

			this.audioElement.addEventListener("ended", ()=>{
				if (this.opts.blockCleanupOnEnd) {
					this.FireAndClearCompletionHandlers();
				} else {
					this.StopAndCleanup();
				}
			}, {once: true});

			await this.audioElement.play();
		} catch (error) {
			console.error(`WebAudioPlayer: failed to start playback for "${opts.contentUri}":`, error);
			this.StopAndCleanup();
		}

		return this;
	}

	Resume() {
		this.audioContext?.resume();
		this.audioElement?.play().catch(e=>console.warn("WebAudioPlayer.Resume:", e));
	}

	Pause() {
		this.audioElement?.pause();
		// Note: do NOT suspend the shared AudioContext here, as it would affect all active WebAudioPlayers.
	}

	StopAndCleanup() {
		// call external handlers first (they might want to eg. read audio-context state before cleanup)
		this.FireAndClearCompletionHandlers();

		if (this.durationLimitTimerID != null) {
			clearTimeout(this.durationLimitTimerID);
			this.durationLimitTimerID = undefined;
		}
		if (this.panSimple_animationTimerID != null) {
			clearInterval(this.panSimple_animationTimerID);
			this.panSimple_animationTimerID = undefined;
		}
		if (this.mute_animationTimerID != null) {
			clearInterval(this.mute_animationTimerID);
			this.mute_animationTimerID = undefined;
		}
		if (this.panSpatial_animationTimerID != null) {
			clearInterval(this.panSpatial_animationTimerID);
			this.panSpatial_animationTimerID = undefined;
		}

		// recording
		if (this.isRecording && this.mediaRecorder) {
			this.mediaRecorder.stop();
			this.isRecording = false;
		}
		this.mediaRecorder = undefined;
		this.recordedChunks = [];
		this.recordingDestinationNode = undefined;

		// audio element
		if (this.audioElement) {
			this.audioElement.pause();
			this.audioElement.src = "";
			this.audioElement = undefined;
		}

		// audio nodes
		if (this.audioSource) { this.audioSource.disconnect(); this.audioSource = undefined; }
		if (this.stereoPannerNode) { this.stereoPannerNode.disconnect(); this.stereoPannerNode = undefined; }
		if (this.pannerNode) { this.pannerNode.disconnect(); this.pannerNode = undefined; }
		if (this.gainNode) { this.gainNode.disconnect(); this.gainNode = undefined; }
		if (this.muteGainNode) { this.muteGainNode.disconnect(); this.muteGainNode = undefined; }

		// Note: do NOT close the shared AudioContext here; just release the local reference.
		this.audioContext = undefined;
	}

	/** Directly set the stereo pan value (-1 = full left, 0 = centre, 1 = full right). */
	/*SetPan(pan: number) {
		if (this.stereoPannerNode) {
			this.stereoPannerNode.pan.value = pan;
		}
	}*/

	/** Set volume (0–1+). */
	SetVolume(volume: number, volumeEffectsAlreadyApplied: boolean) {
		const volume_final = volumeEffectsAlreadyApplied ? volume : ApplyVolumeEffectsToVolumeValue(volume, true, true);
		if (this.gainNode) {
			this.gainNode.gain.value = volume_final;
		}
	}

	// ---- recording ----

	/** Start recording the audio output. Requires opts.enableRecording = true before Start(). */
	async StartRecording(): Promise<void> {
		if (!this.recordingDestinationNode) {
			throw new Error("Recording not enabled. Set opts.enableRecording = true before calling Start().");
		}
		if (this.isRecording) {
			console.warn("WebAudioPlayer: already recording");
			return;
		}
		this.recordedChunks = [];
		this.mediaRecorder = new MediaRecorder(this.recordingDestinationNode.stream, {mimeType: "audio/webm;codecs=opus"});
		this.mediaRecorder.ondataavailable = event=>{
			if (event.data.size > 0) this.recordedChunks.push(event.data);
		};
		this.mediaRecorder.start(100);
		this.isRecording = true;
	}

	async StopRecording(): Promise<void> {
		if (!this.isRecording || !this.mediaRecorder) {
			console.warn("WebAudioPlayer: not currently recording");
			return;
		}
		return new Promise((resolve, reject)=>{
			if (!this.mediaRecorder) { reject(new Error("MediaRecorder not initialized")); return; }
			this.mediaRecorder.onstop = ()=>{
				this.isRecording = false;
				resolve();
			};
			this.mediaRecorder.stop();
		});
	}

	async GetRecordedAudio(): Promise<ArrayBuffer> {
		if (this.recordedChunks.length === 0) throw new Error("No recorded audio available");
		const blob = new Blob(this.recordedChunks, {type: "audio/webm;codecs=opus"});
		return blob.arrayBuffer();
	}

	// ---- private helpers ----

	private _UpdatePan() {
		if (!this.stereoPannerNode) return;
		const {panningType} = this.opts.playOpts;
		const forcedPan =
			this.forcedPanOverride !== undefined ? this.forcedPanOverride :
			panningType == SoundPanningType.simple ? webAudio_forcedPan :
			panningType == SoundPanningType.simple_slow ? webAudio_forcedPan_slow :
			undefined;
		const strength = webAudio_panStrength;
		const panValue =
			forcedPan === "left" ? -strength :
			forcedPan === "right" ? strength :
			0;
		this.stereoPannerNode.pan.value = panValue;
	}

	private _UpdateMute() {
		if (!this.muteGainNode) return;
		const {mutingType} = this.opts.playOpts;
		const forcedMute =
			mutingType == SoundMutingType.simple ? webAudio_forcedMute :
			mutingType == SoundMutingType.simple_slow ? webAudio_forcedMute_slow :
			undefined;
		this.muteGainNode.gain.value = forcedMute ? 0 : 1;
	}

	private _StartSpatialAnimation(spatial: NonNullable<WebAudioPlayer_Opts["spatial"]>) {
		const countType = spatial.cycleComplete_countType ?? CycleCountType.none;
		this.currentAngle = PoolState_startingAngle;
		this.spatialDirection = spatial.initialDirection_clockwise ? 1 : -1;
		this.lastSwitchTime = Date.now();
		const minInterval = spatial.switchDirInterval_min * 1000;
		const maxInterval = spatial.switchDirInterval_max * 1000;
		this.nextSwitchDuration = Math.random() * (maxInterval - minInterval) + minInterval;
		this.cycleCompletionCount = countType !== CycleCountType.none ? (spatial.cycleComplete_countStart ?? 0) : undefined;

		const radius = spatial.distance;
		let lastUpdateTime = Date.now();

		this.panSpatial_animationTimerID = setInterval(()=>{
			if (!this.pannerNode || !this.audioContext) return;

			const currentTime = Date.now();
			const deltaTime = currentTime - lastUpdateTime;
			lastUpdateTime = currentTime;

			// Check if it's time to switch direction
			if (currentTime - this.lastSwitchTime >= this.nextSwitchDuration) {
				this.spatialDirection *= -1;
				this.lastSwitchTime = currentTime;
				this.nextSwitchDuration = Math.random() * (maxInterval - minInterval) + minInterval;
			}

			// Calculate angular velocity based on revolution duration + dynamic adjustment
			const revolutionDuration = Math.max(0.1, spatial.revolutionDuration + (spatial.revolutionDurationAdjustmentGetter?.() ?? 0));
			const radiansPerMS = (2 * Math.PI) / (revolutionDuration * 1000);

			const oldAngle = this.currentAngle;
			this.currentAngle += deltaTime * radiansPerMS * this.spatialDirection;

			// Cycle completion detection
			if (countType !== CycleCountType.none && this.cycleCompletionCount != null) {
				const full360 = 2 * Math.PI;
				const normalizedOld = ((oldAngle % full360) + full360) % full360;
				const normalizedNew = ((this.currentAngle % full360) + full360) % full360;
				const hasCompletedCycle = this.spatialDirection > 0
					? normalizedOld < PoolState_startingAngle && normalizedNew >= PoolState_startingAngle
					: normalizedOld > PoolState_startingAngle && normalizedNew <= PoolState_startingAngle;

				if (hasCompletedCycle) {
					let cancelAnnouncement = false;
					if (countType === CycleCountType.count_up) {
						this.cycleCompletionCount++;
					} else if (countType === CycleCountType.count_down) {
						this.cycleCompletionCount--;
					} else if (countType === CycleCountType.count_down_solveTimeLeft) {
						const overrideResult = spatial.cycleComplete_countOverrideGetter?.();
						if (overrideResult != null) {
							this.cycleCompletionCount = overrideResult;
						} else {
							cancelAnnouncement = true;
						}
					}
					if (!cancelAnnouncement) {
						spatial.onCycleComplete?.(this.cycleCompletionCount);
					}
				}
			}

			// Apply forced-pan override (reads the same global as stereo-panner mode)
			const forcedPan = webAudio_forcedPan;
			const finalAngle =
				forcedPan === "left" ? Math.PI :  // x = -radius (full left)
				forcedPan === "right" ? 0 :       // x = +radius (full right)
				this.currentAngle;

			// Update 3D panner position on the circle
			const x = radius * Math.cos(finalAngle);
			const y = 0;
			const z = radius * Math.sin(finalAngle);
			if (this.pannerNode.positionX) {
				this.pannerNode.positionX.value = x;
				this.pannerNode.positionY.value = y;
				this.pannerNode.positionZ.value = z;
			} else {
				(this.pannerNode as any).setPosition(x, y, z);
			}
		}, 30);
	}

	private _ApplyPlaybackRate() {
		const {playOpts} = this.opts;
		const hasSpeedMultiplier = playOpts.speed_multiplier_min > 0 || playOpts.speed_multiplier_max > 0;
		const hasSpeedDuration = playOpts.speed_duration > 0;

		if (!hasSpeedMultiplier && !hasSpeedDuration) return;
		if (!this.audioElement) return;

		if (hasSpeedDuration) {
			// speed_duration specifies the desired total playback duration in seconds.
			// Rate = fileDuration / speed_duration. We set this once metadata is available.
			const applyRate = ()=>{
				if (!this.audioElement) return;
				const fileDurationSec = this.audioElement.duration;
				if (isFinite(fileDurationSec) && fileDurationSec > 0) {
					this.audioElement.playbackRate = fileDurationSec / playOpts.speed_duration;
				}
			};
			if (isFinite(this.audioElement.duration) && this.audioElement.duration > 0) {
				applyRate();
			} else {
				this.audioElement.addEventListener("loadedmetadata", applyRate, {once: true});
			}
		} else {
			// Direct rate specification via speed_multiplier_min/max
			const minSpeed = Math.max(0.01, playOpts.speed_multiplier_min > 0 ? playOpts.speed_multiplier_min : 1);
			const maxSpeed = Math.max(minSpeed, playOpts.speed_multiplier_max > 0 ? playOpts.speed_multiplier_max : minSpeed);
			const speed = minSpeed + Math.random() * (maxSpeed - minSpeed);
			this.audioElement.playbackRate = speed;
		}
	}
}

// ========================
// WebAudioPlayerPool
// ========================

export interface PlaySoundResult {
	playerId: string;
	playDuration: number; // in seconds
}

/** Manages a set of concurrently-playing WebAudioPlayer instances.
 *  One pool lives on GeneralComp (session-bound), another is a module-level global
 *  used when no session is active. */
export class WebAudioPlayerPool {
	private players = new Map<string, WebAudioPlayer>();

	/** Start playing an audio file. Returns once playback has ended (naturally or via StopSound/StopAll). */
	async PlaySound(opts: WebAudioPlayer_Opts): Promise<PlaySoundResult> {
		const player = new WebAudioPlayer(opts);
		this.players.set(player.id, player);
		await player.Start();

		return new Promise(resolve=>{
			player.AddOneTimeCompletionHandler(()=>{
				// audioContext.currentTime only advances while the context is running (halts on suspend/pause),
				// so it naturally counts only the time audio was actually playing.
				//const playDuration = player.audioContext?.currentTime ?? 0;

				// todo: eventually find a better way (one that works even if loop = true)
				const playDuration = player.audioElement?.currentTime ?? 0;
				this.players.delete(player.id);
				// should we also call player.StopAndCleanup() here? (seems unnecessary?)
				resolve({playerId: player.id, playDuration});
			});
		});
	}

	/** Stop a specific player by id. Returns true if the player was found. */
	StopSound(playerId: string): boolean {
		const player = this.players.get(playerId);
		if (!player) return false;
		player.StopAndCleanup(); // triggers completion handlers
		this.players.delete(playerId);
		return true;
	}

	/** Stop all currently-playing sounds in this pool. */
	StopAll() {
		for (const player of this.players.values()) {
			player.StopAndCleanup(); // triggers completion handlers
		}
		this.players.clear();
	}

	/** Returns true if the pool contains a player with this id. */
	HasPlayer(playerId: string): boolean {
		return this.players.has(playerId);
	}
}

// ========================
// Global no-session pool
// ========================

/** Used when no live session is active (eg. settings panel, EffectPointerInput UI). */
export const webAudioPlayerPool_noSession = new WebAudioPlayerPool();

export function FindWebAudioPlayerInPoolsById(playerId: string): WebAudioPlayer|undefined {
	const liveSession_general_playerPool = liveSession?.Comp(GeneralComp).webAudioPlayerPool;
	return (
		webAudioPlayerPool_noSession.HasPlayer(playerId) ? webAudioPlayerPool_noSession["players"].get(playerId)
		: liveSession_general_playerPool?.HasPlayer(playerId) ? liveSession_general_playerPool["players"].get(playerId)
		: undefined
	);
}